import {
    Box,
    BoxProps,
    HStack,
    Menu,
    MenuProps,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalProps,
    ModalFooter,
    ModalOverlay,
    Spinner,
    Text,
    useDisclosure
} from "@chakra-ui/react";
import {ReactNode, useMemo, useState} from "react";
import {L5Button} from "./CustomButton"
import {FormInput} from "./CustomInput";
import {notEmpty} from "../../helper/utils";

interface ConfirmationModalProps extends Omit<ModalProps, "children"> {
    title?: string
    description: ReactNode
    icon?: string
    children?: ReactNode
    hideIcon?: boolean
    iconColor?: string
}
interface ModalFormProps extends Omit<ModalProps, "children"> {
    title?: string
    children?: ReactNode
    footer?: ReactNode
};
interface CustoMenuProps extends Omit<MenuProps, "children">  {
    children?: ReactNode
    items: {action?: VoidFunction, icon: string, content: string}[]
}
type SuccessModalProps = {
  onClose: VoidFunction;
  isOpen: boolean;
  title: string;
  description: string;
  mainButtonText?: string;
  onMainButtonClick?: VoidFunction;
  trailerComponent?: JSX.Element;
};
interface ConfirmationProps<T> {
    buttonType?: "primary" | "secondary"
    buttonLabel: string
    cancelLabel?: string
    description?: ReactNode
    error?: string
    hideIcon?: boolean
    icon?: string;
    isLoading?: boolean
    isDisabled?: boolean
    inputTransformer?: (value: T) => T
    modalTitle: string;
    onSubmit: (value: T) => void
    placeholder?: string
    submitLabel?: string
    size?: string
    validator?: (v: T) => string | undefined
}

const bodyProps: BoxProps = {
    px:{base: "1rem", md: "2rem"},
    py: "1rem",
    maxW: {base: "100%"},
    borderRadius: "12px",
    display: "flex",
    flexDir: "column",
    gap: "1rem",
    alignItems: "center",
};
export function ModalForm(props: ModalFormProps) {
    return <Modal
        isOpen={props.isOpen}
        onClose={props.onClose}
        isCentered
        trapFocus
        size={props.size ?? "md"}
    >
        <ModalOverlay/>
        <ModalContent zIndex="overlay">
            {props.title && <ModalHeader>{props.title}</ModalHeader>}
            <ModalCloseButton insetInlineEnd={{base:"1rem", md: "2rem"}} insetBlockStart=".75rem"/>
            <ModalBody {...bodyProps}>
                {props.children}
            </ModalBody>
            {props.footer && <ModalFooter
                flexDir="column-reverse"
                alignItems="stretch"
                gap=".5rem"
                px={{base: "1rem", md: "2rem"}}
            >{props.footer}</ModalFooter>}
        </ModalContent>
    </Modal>;
}

export function ConfirmationModal(props: ConfirmationModalProps) {
    return <ModalForm
        isOpen={props.isOpen}
        onClose={props.onClose}
        size={props.size}
        title={props.title}
        footer={props.children}
    >
        {!props.hideIcon && <svg width="116" height="116" style={{color: props.iconColor ?? "var(--chakra-colors-red-400)"}}>
            <use xlinkHref={`#${props.icon ?? "warning-thin"}`}/>
        </svg>}
        {props.description}
    </ModalForm>;
}

export function DefaultDescription(props: {title:string, desc: string} & BoxProps) {
    return <Box className="stack" lineHeight="1.375" textAlign={props.textAlign} css={{"--s-gap": props.gap ?? ".5rem"}} alignSelf={props.alignSelf}>
        <Text color="dark.200" fontWeight={700}> {props.title} </Text>
        <Text color="#454545" fontSize=".95em"> {props.desc}</Text>
    </Box>;
}

function SuccessModal(p: SuccessModalProps) {
    return <ConfirmationModal
        isOpen={p.isOpen}
        onClose={p.onClose}
        icon="checked"
        description={<DefaultDescription title={p.title} desc={p.description} textAlign="center" gap="1rem"/>}
        iconColor="var(--chakra-colors-primary-500)"
    >
        {p.trailerComponent}
        <L5Button type="primary" onClick={p.onMainButtonClick ?? p.onClose}>
            {p.mainButtonText ?? "Okay"}
        </L5Button>
    </ConfirmationModal>;
}

export function ConfirmationButton(props: ConfirmationProps<string>) {
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [inputValue, setInputValue] = useState<string|null>(null);
    const error = useMemo(function () {
        if (!inputValue) {
            return;
        }
        if (props.validator) {
            return props.validator(inputValue);
        }
        if (notEmpty(props.placeholder) && !notEmpty(inputValue)) {
            return "This input should not be empty";
        }
        if (props.error) {
            return props.error;
        }
    }, [inputValue, props]);
    const input = useMemo(function () {
        if (props.inputTransformer && inputValue) {
            return props.inputTransformer(inputValue);
        }
        return inputValue ?? "";
    }, [inputValue]);
    function handleConfirm() {
        const trimmed = input.trim();
        if (trimmed || props.placeholder === undefined) {
            props.onSubmit(trimmed);
            setInputValue("");
            onClose();
        }
    }
    function handleCancel() {
        setInputValue("");
        onClose();
    }
    return <>
            <L5Button
                type={props.buttonType ?? "primary"}
                isDisabled={props.isDisabled}
                onClick={onOpen}
            >
                {props.isLoading ? <Spinner/> : props.buttonLabel}
            </L5Button>
            <ConfirmationModal
                isOpen={isOpen}
                size={props.size ?? "lg"}
                onClose={handleCancel}
                title={props.modalTitle}
                icon={props.icon}
                hideIcon={props.hideIcon}
                description={
                    <HStack flexDir="column" alignItems="center" gap={3}>
                        {props.description}
                        {props.placeholder && <FormInput
                            type="text"
                            data={{
                                h: "56px",
                                placeholder: props.placeholder,
                                currentValue: input,
                                error,
                                onUpdate: (v) => setInputValue(v)
                            }}
                        />}
                    </HStack>
                }
            >
                <L5Button type="secondary" onClick={handleCancel}>
                    {props.cancelLabel ?? "Cancel"}
                </L5Button>
                <L5Button
                    onClick={handleConfirm}
                    type="primary"
                    isDisabled={notEmpty(error)}
                >
                    {props.submitLabel}
                </L5Button>
        </ConfirmationModal>
    </>;
};


export function CustoMenu(p: CustoMenuProps) {
    const clone: any = Object.assign({}, p);
    delete clone.children;
    delete clone.items;
    return <Menu {...clone}>
        <MenuButton flex="0 0 auto">{p.children !== undefined
            ? p.children
            : <svg width="24" height="24" aria-labelledby="#menu_title">
                <title id="menu_title">View the available actions</title>
                <use xlinkHref="#v_dots"/>
            </svg>
        }</MenuButton>
        <MenuList>
            {p.items.map( (v, idx) => <MenuItem key={idx} onClick={v.action}>
                <HStack gap=".5rem">
                    <svg width="16" height="16" aria-hidden="true" style={{flex: "0 0 auto"}}>
                        <use xlinkHref={`#${v.icon}`} />
                    </svg>
                    <Text className="one-line" fontSize="0.9em">{v.content}</Text>
                </HStack>
            </MenuItem>)}
        </MenuList>
    </Menu>;
}

export function Description(p: {title: string, children?: ReactNode, description: string}) {
    return <div className="stack">
        <Text fontWeight="bold" color="dark.200" textAlign="center">
            {p.title}
        </Text>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            {p.description}
        </Text>
        {p.children}
    </div>;
}
export default SuccessModal;
